import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import appConfig from '../../../config';
import Fuse from 'fuse.js';
import {getArtwork, removeArtwork} from '../../../api.js';
import {
  initialState,
  applicationSelector,
  updateCurrentKonvaObject,
  doSingleUpload
} from '../../../slices/application';
import {useShadowRoot} from '../../../contexts/ShadowRootContext';
import FileUpload from '../../FileUpload/FileUpload.jsx';
import CancelBtn from '../../Buttons/AddEditCancel';
import {extractCanvasMeasurementsFromDesign} from '../../../utils/utils';

const SelectArt = () => {
  const dispatch = useDispatch();
  const shadowRoot = useShadowRoot();
  const cdnUri = appConfig.cdnUri;
  const [artworks, setArtworks] = useState([]);
  const [query, setQuery] = useState('');
  const [filteredArtworks, setFilteredArtworks] = useState(artworks);
  const [artSrc, setArtSrc] = useState('');
  const [isDisabled, setIsDisabled] = useState(true);
  const {
    design,
    currentKonvaObject,
    uuid,
    isArtUpdated,
    singleUploadCheck,
    recentUploads
  } = useSelector(applicationSelector);

  // Helper function to compare upload times for sorting
  function compareUploadTimes(a, b) {
    if (a.uploadedAt < b.uploadedAt) {
      return 1;
    }
    if (a.uploadedAt > b.uploadedAt) {
      return -1;
    }
    return 0;
  }

  // Sort user artwork by upload time
  function sortUserArtwork(list) {
    return list.sort(compareUploadTimes);
  }

  useEffect(() => {
    const fetchArtwork = async () => {
      const res = await getArtwork(uuid);
      if (res) {
        setArtworks(
          sortUserArtwork(res.data.user_artworks).concat(
            res.data.default_artworks
          )
        );
      }
    };
    fetchArtwork();
  }, [isArtUpdated, recentUploads]);

  const {tubeWidth, tubeHeight, drawableWidth, drawableHeight, ellipseRX} =
    extractCanvasMeasurementsFromDesign(design);

  function removeArtworkAndUpdateState(artworkId) {
    removeArtwork(artworkId).then(() => {
      const newArtworks = artworks.filter(
        (artwork) => artwork.artwork_id !== artworkId
      );
      setArtworks(newArtworks);
    });
  }

  function selectId(src, imgWidth, imgHeight, artworkId) {
    let width = imgWidth;
    let height = imgHeight;
    let stock = false;
    if (src.includes('default-clipart')) {
      // Strip any surrounding quotes from artworkId (API may return quoted IDs)
      const cleanId = artworkId?.replace(/^"|"$/g, '');
      // Use attribute selector since artwork IDs may start with numbers (invalid for CSS #id selectors)
      const imageElement = shadowRoot.querySelector?.(`[id="${cleanId}"]`);
      width = imageElement?.naturalWidth || imgWidth;
      height = imageElement?.naturalHeight || imgHeight;
      stock = true;
    }
    setIsDisabled(false);
    setArtSrc({src: src, imgWidth: width, imgHeight: height, stock: stock});
  }

  // focus and other focus psuedo selectors don't quite work right here on Safari
  function focusSafari(e) {
    Array.from(shadowRoot.querySelectorAll?.('.hs-form-art-image.focus') || []).forEach(
      (el) => el.classList.remove('focus')
    );
    e.currentTarget.className += ' focus';
  }

  // Combined handler for button click - handles both focus styling and selection
  function handleArtworkClick(src, imgWidth, imgHeight, artworkId, e) {
    focusSafari(e);
    selectId(src, imgWidth, imgHeight, artworkId);
  }

  function resize(img) {
    const resized = {src: img.src};

    // fit img within it + padding
    const imageAspect = img.imgWidth / img.imgHeight;
    const drawableAspect = drawableWidth / drawableHeight;
    if (drawableAspect > imageAspect) {
      // height constrained
      resized.imgHeight = Math.round(drawableHeight * 0.8);
      resized.imgWidth = Math.round(imageAspect * resized.imgHeight);
    } else {
      // width constrained
      resized.imgWidth = Math.round(drawableWidth * 0.8);
      resized.imgHeight = Math.round(resized.imgWidth / imageAspect);
    }
    return resized;
  }

  // Set selected image and continue to image editing page
  function setArtwork() {
    const resizedImage = resize(artSrc);

    const color = initialState.printColors.find(
      ({value}) => value === design.printColor
    ).rgb;

    // check if currentKonvaObject
    let id = design.lastId + 1;
    let x = ((tubeWidth - ellipseRX) * 0.5) / tubeWidth;
    let y = 0.5;
    let invert = true;
    let rotation = 0;
    let threshold = artSrc.stock ? 0.2 : 0.8;
    if (currentKonvaObject) {
      id = currentKonvaObject.id;
      x = currentKonvaObject.x;
      y = currentKonvaObject.y;
      invert = currentKonvaObject.invert;
      rotation = currentKonvaObject.rotation;
      threshold = currentKonvaObject.threshold;
    }

    let scaleX = 0.8;
    let scaleY = 0.8;
    const imageAspect = resizedImage.imgWidth / resizedImage.imgHeight;
    if (imageAspect > drawableWidth / drawableHeight) {
      scaleY = (scaleX * tubeWidth) / imageAspect / tubeHeight;
    } else {
      scaleX = (scaleY * tubeHeight * imageAspect) / tubeWidth;
    }

    const imageObject = {
      id: String(id),
      key: String(id),
      type: 'image',
      x: x,
      y: y,
      scaleX: scaleX,
      scaleY: scaleY,
      skewX: 0,
      skewY: 0,
      height: artSrc.imgHeight,
      width: artSrc.imgWidth,
      offsetX: artSrc.imgWidth / 2,
      offsetY: artSrc.imgHeight / 2,
      threshold: threshold, // they can't see this before adding to page
      url: resizedImage.src,
      rotation: rotation,
      fill: 'transparent',
      selectColor: color,
      invert: invert,
      isSelected: false
    };

    // set the selected image src, to be used on the edit img
    dispatch(updateCurrentKonvaObject(imageObject));
  }

  // Auto-select first artwork after single upload
  useEffect(() => {
    if (singleUploadCheck) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional: auto-select uploaded artwork
      selectId(
        `${cdnUri}/${artworks[0]?.processed_location}`,
        artworks[0]?.imageWidth,
        artworks[0]?.imageHeight
      );
    }
  }, [artworks]);

  // Auto-add artwork after single upload selection
  useEffect(() => {
    if (singleUploadCheck) {
      setArtwork();
      dispatch(doSingleUpload(false));
    }
  }, [artSrc]);

  const searchArt = (query) => {
    const search = new Fuse(artworks, {keys: ['processed_location']});
    const results = search.search(query);

    // reformat results to match artworks
    return results.map(function (x) {
      return x.item;
    });
  };

  const filterArt = () => {
    if (query != null && query != '') setFilteredArtworks(searchArt(query));
    else setFilteredArtworks(artworks);
  };

  useEffect(() => {
    getArtwork();
  }, [uuid]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional: filter artwork list when search query changes
    filterArt();
  }, [artworks, query]);

  return (
    <div className="hs-form-container">
      <div className="hs-form-header">
        <h2 className="hs-form-heading has-bottom-border">ADD ART</h2>
        <FileUpload
          content="Upload an Image"
          classes="btn uix-btn-outline-link btn-sm input-radius hs-form-heading-link"
          id="artUpload"
        />
      </div>
      <div className="hs-form-body set-overflow">
        <div className="hs-form-control-group search">
          <input
            type="text"
            className="form-control hs-form-control"
            id="PrintSearch"
            name="PrintSearch"
            defaultValue=""
            placeholder="Search for Artwork"
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="hs-form-art-list">
          {filteredArtworks.map((art) => {
            return (
              <div
                className="hs-form-art-item"
                key={art.artwork_id}>
                <div className="hs-art-item-container">
                  {art.stock ? null : (
                    <button
                      type="button"
                      className="btn btn-outline-secondary hs-form-art-clear"
                      tabIndex="-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeArtworkAndUpdateState(art.artwork_id);
                      }}>
                      <FontAwesomeIcon icon={['fas', 'times']} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => handleArtworkClick(
                      `${cdnUri}/${art?.processed_location}`,
                      art?.imageWidth,
                      art?.imageHeight,
                      art?.artwork_id,
                      e
                    )}
                    className={`btn btn-outline-secondary input-radius hs-form-art-image ${
                      art.stock ? 'stock-art-button' : ''
                    } ${
                      recentUploads.includes(art.artwork_id)
                        ? 'recent-artwork'
                        : ''
                    }`}>
                    <img
                      id={art.artwork_id?.replace(/^"|"$/g, '')}
                      alt={art.stock ? 'stock art' : 'user art'}
                      className={art.stock ? 'stock-art' : ''}
                      src={`${cdnUri}/${art?.processed_location}`}
                    />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="hs-form-footer">
        <CancelBtn />
        <button
          type="submit"
          className="btn btn-primary btn-sm input-radius float-end"
          onClick={setArtwork}
          disabled={isDisabled}>
          Add Image
        </button>
      </div>
    </div>
  );
};

export default SelectArt;
